/**
 * Formats a number into a string with commas as thousands separators.
 * @param value The number to format.
 * @returns A formatted string representation of the number, or '0' if the input is invalid.
 */
function _formatNumber(value: number | undefined | null): string {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0'
  }
  return value.toLocaleString('en-US')
}

/**
 * Masks an EIN for display, showing only the last 4 digits.
 * @example maskEin('12-3456789') => '**-***6789'
 * @example maskEin('123456789') => '**-***6789'
 */
export function maskEin(ein: string | null | undefined): string {
  if (!ein) return '-'
  const digits = ein.replace(/\D/g, '')
  if (digits.length < 4) return '*'.repeat(ein.length)
  return `**-***${digits.slice(-4)}`
}

/**
 * Formats a person's full name from first and last name parts.
 * @example _formatFullName('John', 'Doe') => 'John Doe'
 * @example _formatFullName('John', null) => 'John'
 * @example _formatFullName(null, null) => '—'
 */
function _formatFullName(
  firstname: string | null | undefined,
  lastname: string | null | undefined,
  fallback = '—',
): string {
  const first = firstname?.trim()
  const last = lastname?.trim()
  if (first && last) return `${first} ${last}`
  return first || last || fallback
}

/**
 * Formats a full company name from business_name and designator.
 *
 * Canonical per `src/features/companies/AGENTS.md` — do not write a
 * local company-name formatter, and do not delete this one for being unused.
 *
 * @example formatCompanyName('Acme', 'LLC') => 'Acme LLC'
 * @example formatCompanyName('Acme', null) => 'Acme'
 * @example formatCompanyName(null, null) => '—'
 * @public
 */
export function formatCompanyName(
  businessName: string | null | undefined,
  designator: string | null | undefined,
  fallback = '—',
): string {
  const name = businessName?.trim()
  const suffix = designator?.trim()
  if (name && suffix) return `${name} ${suffix}`
  return name || fallback
}

const COMPANY_TYPE_MAP: Record<string, string> = {
  llc: 'LLC',
  ccorp: 'C Corp',
  'c-corp': 'C Corp',
  'c corp': 'C Corp',
  c_corp: 'C Corp',
  scorp: 'S Corp',
  's-corp': 'S Corp',
  s_corp: 'S Corp',
  llp: 'LLP',
}

/**
 * Canonical company type formatter. Use this everywhere — do not create local versions.
 * @example formatCompanyType('ccorp') => 'C Corp'
 * @example formatCompanyType('llc') => 'LLC'
 * @public
 */
export function formatCompanyType(type: string | null | undefined, fallback = '—'): string {
  if (!type) return fallback
  const lower = type.toLowerCase()
  return COMPANY_TYPE_MAP[lower] ?? type
}

/**
 * Masks a Tax ID (SSN/ITIN/EIN/FTIN) for display, showing only the last 4 characters.
 * Foreign ids keep their letters: the last four are taken from the raw value
 * with only separators (spaces, dashes, dots) dropped.
 * @example maskTaxId('123-45-6789') => '***-**-6789'
 * @example maskTaxId('RSSSFO85M41F205Z') => '***205Z'
 */
export function maskTaxId(taxId: string | null | undefined): string {
  if (!taxId) return '-'
  const cleaned = taxId.replace(/[^A-Za-z0-9]/g, '')
  if (cleaned.length < 4) return '*'.repeat(taxId.length)
  const lastFour = cleaned.slice(-4)
  if (/^\d{9}$/.test(cleaned) && taxId.includes('-')) {
    return `***-**-${lastFour}`
  }
  return `***${lastFour}`
}

const USD_WHOLE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})
const USD_CENTS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * US dollars for display: `$299`, `$2,499`, and cents only when there are any
 * (`$19.50`). The one money formatter for prices a customer sees: onboarding
 * packages, the services catalog, deadlines and tax filing cards (spec K2).
 * @public
 */
export function formatUsd(dollars: number): string {
  return Number.isInteger(dollars) ? USD_WHOLE.format(dollars) : USD_CENTS.format(dollars)
}
